import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useShiftStore } from '@/stores/shift';
import { useHistoryStore, type ShiftRow } from '@/stores/history';
import { fetchDashboard } from '@/services/api/dash';
import type { DashboardData } from '@/models/dashboard';
import { parseAmountValue } from '@/lib/amount';
import { greetingFor } from '@/lib/greeting';
import { BarChart, PercentileBar, Sparkline } from '@/components/Charts';

const MONTHS_FR_SHORT = [
  'jan', 'fév', 'mar', 'avr', 'mai', 'juin',
  'juil', 'août', 'sept', 'oct', 'nov', 'déc',
];

function dayMonth(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS_FR_SHORT[d.getMonth()] ?? ''}`;
}

function nonEmpty(v: string | null | undefined): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === '0' || s === '0.0' || s === '0.00') return null;
  return s;
}

function num(v: string | null | undefined): number | null {
  const s = nonEmpty(v);
  if (s == null) return null;
  const n = parseAmountValue(s);
  return Number.isFinite(n) ? n : null;
}

function KpiCard({
  label,
  value,
  hint,
  sparkline,
}: {
  label: string;
  value: string;
  hint?: string | null;
  sparkline?: number[];
}) {
  return (
    <div className="bg-brand-dark/60 rounded-2xl p-3 shadow-card flex flex-col gap-1">
      <p className="text-[10px] uppercase tracking-wide text-white/55">
        {label}
      </p>
      <p className="text-2xl font-semibold leading-none">{value}</p>
      {sparkline && <Sparkline data={sparkline} height={24} />}
      {hint && <p className="text-[11px] text-white/60">{hint}</p>}
    </div>
  );
}

export function HomePage() {
  const profile = useAuthStore((s) => s.profile);
  const currentShift = useShiftStore((s) => s.currentShift);
  const cachedShiftTotal = useShiftStore((s) => s.cachedShiftTotal);
  const verifyCurrentShift = useShiftStore((s) => s.verifyCurrentShift);
  const historyRows = useHistoryStore((s) => s.rows);
  const refreshHistory = useHistoryStore((s) => s.refresh);

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [compareAllPeers, setCompareAllPeers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    void verifyCurrentShift(profile.uid);
    void refreshHistory(profile.uid);
  }, [profile, verifyCurrentShift, refreshHistory]);

  useEffect(() => {
    if (!profile) return;
    const mdType = compareAllPeers ? 'all' : profile.mdType || 'cfpc';
    setLoading(true);
    setError(null);
    fetchDashboard({ userId: profile.uid, mdType })
      .then(setDashboard)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [profile, compareAllPeers]);

  const submittedRows = useMemo<ShiftRow[]>(
    () => historyRows.filter((r) => !r.isPending),
    [historyRows],
  );

  const recentByDate = useMemo(
    () =>
      [...submittedRows]
        .sort((a, b) => a.dateStarted.localeCompare(b.dateStarted))
        .slice(-12),
    [submittedRows],
  );

  const revenueBars = useMemo(
    () =>
      recentByDate.map((r) => ({
        label: dayMonth(r.dateStarted),
        value: parseAmountValue(r.amount),
      })),
    [recentByDate],
  );

  const revenueSparkline = useMemo(
    () => recentByDate.map((r) => parseAmountValue(r.amount)),
    [recentByDate],
  );

  if (!profile) return null;

  const hasShift = !!currentShift.dateStarted;
  const shiftCount = submittedRows.length;
  const computedTotal = submittedRows.reduce(
    (sum, r) => sum + parseAmountValue(r.amount),
    0,
  );
  const computedAvg = shiftCount ? Math.round(computedTotal / shiftCount) : 0;

  const billedSoFar = num(dashboard?.userBilledSoFar) ?? computedTotal;
  const avgPerShift = num(dashboard?.userAvgCash) ?? computedAvg;
  const patientsSeen = num(dashboard?.userPatientsSeen);
  const patientsPerShift = nonEmpty(dashboard?.userAvgPatientsPerShift);
  const cashPerPatient = nonEmpty(dashboard?.userAvgCashPerPatient);
  const patientPerHour = nonEmpty(dashboard?.userPatientPerHour);
  const shiftLen = nonEmpty(dashboard?.userAvgShiftLength);
  const bestCash = num(dashboard?.userBestCash);

  const hasData = !loading && (shiftCount > 0 || dashboard != null);

  // Percentiles can come back as strings like "85" or null when peer data is
  // unavailable. Show only the ones we got.
  const percentiles = [
    { label: '$ / quart', v: dashboard?.userAvgCashRank },
    { label: '$ / patient', v: dashboard?.userAvgCashPerPatientRank },
    { label: 'Patients / quart', v: dashboard?.userAvgPatientsPerShiftRank },
    { label: 'Nouveaux patients', v: dashboard?.userNewPatientsRank },
  ]
    .map((p) => ({ label: p.label, percentile: num(p.v) }))
    .filter((p): p is { label: string; percentile: number } => p.percentile != null);

  return (
    <div className="p-4 flex flex-col gap-4 pb-24">
      <header>
        <h1 className="text-2xl font-semibold">
          {greetingFor(profile.firstName)}
        </h1>
        <p className="text-xs text-white/60 mt-0.5">{profile.email}</p>
      </header>

      <section className="bg-gradient-to-br from-brand-dark to-brand-darkest rounded-2xl p-4 shadow-card border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm uppercase tracking-wide text-white/55">
            Quart en cours
          </h2>
          {hasShift && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-brand-orange/20 text-brand-orange">
              Actif
            </span>
          )}
        </div>
        {hasShift ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-semibold leading-none">
                  {cachedShiftTotal} $
                </p>
                <p className="text-xs text-white/60 mt-1">
                  {currentShift.place ?? '—'}
                </p>
              </div>
              <p className="text-xs text-white/55 text-right">
                Depuis
                <br />
                {currentShift.dateStarted
                  ? new Date(currentShift.dateStarted).toLocaleString('fr-CA', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/shift"
                className="flex-1 rounded-pill bg-brand-orange text-white font-semibold py-2 text-center text-sm"
              >
                Voir le quart
              </Link>
              <Link
                to="/new-patient"
                className="flex-1 rounded-pill bg-white text-brand-darkest font-semibold py-2 text-center text-sm"
              >
                + Patient
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-white/65">Aucun quart en cours.</p>
            <Link
              to="/new-patient"
              className="rounded-pill bg-brand-orange text-white font-semibold py-2 text-center text-sm"
            >
              Démarrer un quart
            </Link>
          </div>
        )}
      </section>

      {loading && (
        <section className="bg-brand-dark/40 rounded-2xl p-4 shadow-card">
          <p className="text-sm text-white/60">Chargement…</p>
        </section>
      )}
      {error && (
        <section className="bg-red-500/10 border border-red-300/30 rounded-2xl p-3">
          <p className="text-xs text-red-200">{error}</p>
        </section>
      )}

      {!loading && hasData && (
        <>
          <section className="grid grid-cols-2 gap-2">
            <KpiCard
              label="Facturé à vie"
              value={`${billedSoFar.toLocaleString('fr-CA')} $`}
              hint={bestCash != null ? `Meilleur quart : ${bestCash} $` : undefined}
            />
            <KpiCard
              label="Moyenne / quart"
              value={`${avgPerShift} $`}
              sparkline={revenueSparkline}
              hint={shiftCount ? `${shiftCount} quarts soumis` : undefined}
            />
            <KpiCard
              label="Patients vus"
              value={patientsSeen != null ? String(patientsSeen) : '—'}
              hint={
                patientsPerShift ? `${patientsPerShift} / quart` : undefined
              }
            />
            <KpiCard
              label="$ / patient"
              value={cashPerPatient ? `${cashPerPatient} $` : '—'}
              hint={patientPerHour ? `${patientPerHour} pts/h` : undefined}
            />
          </section>

          {revenueBars.length > 0 && (
            <section className="bg-brand-dark/40 rounded-2xl p-4 shadow-card">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold">
                  Revenus — derniers quarts
                </h2>
                <span className="text-xs text-white/55">
                  {revenueBars.length} quarts
                </span>
              </div>
              <BarChart
                data={revenueBars}
                formatValue={(v) => `${v} $`}
              />
            </section>
          )}

          {percentiles.length > 0 && (
            <section className="bg-brand-dark/40 rounded-2xl p-4 shadow-card flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Vs pairs</h2>
                <label className="text-[11px] text-white/65 flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={compareAllPeers}
                    onChange={(e) => setCompareAllPeers(e.target.checked)}
                    className="accent-brand-orange w-3.5 h-3.5"
                  />
                  Tous spécialités
                </label>
              </div>
              <div className="flex flex-col gap-2">
                {percentiles.map((p) => (
                  <PercentileBar
                    key={p.label}
                    label={p.label}
                    percentile={p.percentile}
                  />
                ))}
              </div>
            </section>
          )}

          {(dashboard?.peersAvgCash || shiftLen) && (
            <section className="bg-brand-dark/40 rounded-2xl p-4 shadow-card flex flex-col gap-2">
              <h2 className="text-sm font-semibold mb-1">
                Détails comparatifs
              </h2>
              {dashboard?.peersAvgCash && (
                <DetailRow
                  label="Moyenne pairs"
                  user={dashboard?.userAvgCash}
                  peer={dashboard?.peersAvgCash}
                  unit="$"
                />
              )}
              {dashboard?.peersAvgPatientsPerShift && (
                <DetailRow
                  label="Patients / quart"
                  user={dashboard?.userAvgPatientsPerShift}
                  peer={dashboard?.peersAvgPatientsPerShift}
                />
              )}
              {dashboard?.peersAvgCashPerPatient && (
                <DetailRow
                  label="$ / patient"
                  user={dashboard?.userAvgCashPerPatient}
                  peer={dashboard?.peersAvgCashPerPatient}
                  unit="$"
                />
              )}
              {dashboard?.peersPatientPerHour && (
                <DetailRow
                  label="Patients / heure"
                  user={dashboard?.userPatientPerHour}
                  peer={dashboard?.peersPatientPerHour}
                />
              )}
              {dashboard?.peersAvgShiftLength && (
                <DetailRow
                  label="Durée moyenne"
                  user={dashboard?.userAvgShiftLength}
                  peer={dashboard?.peersAvgShiftLength}
                  unit="h"
                />
              )}
              {dashboard?.lastUpdated && (
                <p className="text-[10px] text-white/40 mt-1">
                  Mis à jour : {dashboard.lastUpdated.slice(0, 16).replace('T', ' ')}
                </p>
              )}
            </section>
          )}
        </>
      )}

      {!loading && !hasData && !error && (
        <section className="bg-brand-dark/40 rounded-2xl p-6 shadow-card text-center">
          <p className="text-sm text-white/70">
            Vos statistiques apparaîtront ici après votre premier quart soumis.
          </p>
        </section>
      )}
    </div>
  );
}

function DetailRow({
  label,
  user,
  peer,
  unit,
}: {
  label: string;
  user: string | null | undefined;
  peer: string | null | undefined;
  unit?: string;
}) {
  const u = nonEmpty(user);
  const p = nonEmpty(peer);
  if (!u && !p) return null;
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/70">{label}</span>
      <span className="font-semibold tabular-nums">
        {u ?? '—'}
        {unit ? ` ${unit}` : ''}
        <span className="text-white/40 font-normal ml-2 text-xs">
          (pairs {p ?? '—'}
          {unit ? ` ${unit}` : ''})
        </span>
      </span>
    </div>
  );
}
